import type { ScheduleItem, ScheduleItemSegment } from '../types/scheduleItem.types';
import type { SegmentCreationResult } from '../types/scheduleItem.types';
import type { SegmentFormData } from '../types/scheduleItem.types';

/**
 * Match a form segment to a persisted segment by content (title, service code, bill-as, hours).
 */
function findMatchingApiSegment(
  formSeg: Pick<
    SegmentFormData,
    'segmentTitle' | 'serviceCodeIdentity' | 'billAs' | 'estimatedHours'
  >,
  apiSegments: ScheduleItemSegment[],
  usedIds: Set<number>,
): ScheduleItemSegment | undefined {
  return apiSegments.find(
    (api) =>
      !usedIds.has(api.segmentId) &&
      (api.segmentTitle ?? '').trim() === formSeg.segmentTitle.trim() &&
      (api.serviceCodeId ?? 0) === formSeg.serviceCodeIdentity &&
      api.billAs === formSeg.billAs &&
      Number(api.estimatedHours) === Number(formSeg.estimatedHours),
  );
}

/**
 * Apply backend segment identities to form rows so updates/commits do not insert duplicates.
 */
export function mergeSegmentIdsIntoFormSegments(
  formSegments: SegmentFormData[],
  apiSegments: ScheduleItemSegment[] = [],
  segmentResults?: SegmentCreationResult[],
): SegmentFormData[] {
  const usedApiIds = new Set<number>();

  return formSegments.map((formSeg, index) => {
    if (formSeg.segmentId && formSeg.segmentId > 0) {
      usedApiIds.add(formSeg.segmentId);
      return formSeg;
    }

    const fromCreateResult =
      segmentResults?.[index]?.segmentId ??
      segmentResults?.find(
        (r) => r.displayText.trim() === formSeg.segmentTitle.trim(),
      )?.segmentId;

    if (fromCreateResult && fromCreateResult > 0) {
      usedApiIds.add(fromCreateResult);
      return { ...formSeg, segmentId: fromCreateResult };
    }

    const matched = findMatchingApiSegment(formSeg, apiSegments, usedApiIds);
    if (matched) {
      usedApiIds.add(matched.segmentId);
      return { ...formSeg, segmentId: matched.segmentId };
    }

    return formSeg;
  });
}

/**
 * Build schedule-item segment list after create when refetch has not returned yet.
 */
export function buildSegmentsFromCreateResponse(
  formSegments: SegmentFormData[],
  segmentResults?: SegmentCreationResult[],
  fallbackSegmentId?: number,
): ScheduleItemSegment[] {
  const validFormSegments = formSegments.filter(
    (seg) => seg.segmentTitle?.trim().length > 0 && seg.serviceCodeIdentity > 0,
  );

  return validFormSegments.map((seg, index) => {
    const matchedResult =
      segmentResults?.[index] ??
      segmentResults?.find(
        (r) => r.displayText.trim() === seg.segmentTitle.trim(),
      );

    const segmentId =
      matchedResult?.segmentId ??
      (index === 0 ? fallbackSegmentId : undefined) ??
      0;

    return {
      segmentId,
      segmentTitle: seg.segmentTitle,
      serviceCodeId: seg.serviceCodeIdentity,
      billAs: seg.billAs,
      estimatedHours: seg.estimatedHours,
    };
  });
}

/**
 * Resolve segment IDs from the linked schedule item before sending an update request.
 */
export function resolveSegmentIdsForUpdate(
  formSegments: SegmentFormData[],
  item?: ScheduleItem | null,
): SegmentFormData[] {
  const apiSegments = item?.segments ?? [];
  const legacySegmentId = item?.segmentId;

  if (apiSegments.length === 0 && legacySegmentId) {
    return formSegments.map((seg, index) =>
      index === 0 && (!seg.segmentId || seg.segmentId <= 0)
        ? { ...seg, segmentId: legacySegmentId }
        : seg,
    );
  }

  return mergeSegmentIdsIntoFormSegments(formSegments, apiSegments);
}

/**
 * Returns true when any form row is missing a persisted segment id.
 */
export function formSegmentsNeedIdSync(
  formSegments: SegmentFormData[],
  item?: ScheduleItem | null,
): boolean {
  const hasPersistedSegments =
    (item?.segments?.length ?? 0) > 0 || (item?.segmentId ?? 0) > 0;

  if (!hasPersistedSegments) {
    return false;
  }

  return formSegments.some((seg) => !seg.segmentId || seg.segmentId <= 0);
}
